import {
  Avatar,
  Box,
  CircularProgress,
  Container,
  CssBaseline,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  IconButton,
  LinearProgress,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import EditIcon from '@mui/icons-material/Edit';
import FaceIcon from '@mui/icons-material/Face';
import BusinessIcon from '@mui/icons-material/Business';
import { useQuery } from '@apollo/client';

import { graphql } from '../../__generated__';
import Copyright from '../../components/common/Copyright';
import configs from '../../conf/config';
import { useEffect, useState } from 'react';
import TenantChooserDialog from './TenantChooserDialog';
import AlertDialog from '../../components/common/AlertDialog';
import { convertBalances } from '../../util/balance';
import { BalanceInfo, CurrencyData } from '../../types';
import SavingsIcon from '@mui/icons-material/Savings';

export const getSettingsQuery = graphql(/* GraphQL */ `
  query Settings {
    agent {
      id
      email
      name
      role
      tenant
    }
    tenant {
      name
      id
    }
  }
`);
export const getBalanceQuery = graphql(/* GraphQL */ `
  query Balances {
    account {
      balance
      currency
    }
    accountBalance {
      label
      symbol
      name
      value
    }
  }
`);

const SettingsContainer = () => {
  const [showTenants, setShowTenants] = useState(false);
  const [showTenantChange, setShowTenantChange] = useState(false);
  const { data, loading, error, refetch } = useQuery(getSettingsQuery);
  const {
    data: balanceData,
    loading: loadingBalance,
    error: balanceError,
    refetch: balanceRefetch,
  } = useQuery(getBalanceQuery);
  const [accountBalance, setAccountBalance] = useState<BalanceInfo[]>([]);

  useEffect(() => {
    const interval = setInterval(async () => {
      await balanceRefetch();
    }, 5000);

    return () => clearInterval(interval);
  }, [balanceRefetch]);

  useEffect(() => {
    if (balanceData?.accountBalance) {
      const items = convertBalances(
        balanceData.accountBalance as CurrencyData[]
      );
      setAccountBalance(items);
    }
  }, [balanceData?.accountBalance, setAccountBalance]);

  const onNewTenantSelected = async (value: string) => {
    setShowTenants(false);
    await refetch();
    await balanceRefetch();
    setShowTenantChange(true);
  };

  const onCloseTenants = async (value?: string) => {
    setShowTenants(false);
  };

  let displayError;

  if (error || balanceError) {
    displayError = error?.message || balanceError?.message;
  }

  const { agent, tenant } = data || {};

  return (
    <Container component="main">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 2,
          marginBottom: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {displayError && (
          <Typography variant="caption" gutterBottom color="red">
            {displayError}
          </Typography>
        )}
        <List
          sx={{ width: '100%', maxWidth: 700, bgcolor: 'background.paper' }}
        >
          Account
          <ListItem
            alignItems="flex-start"
            secondaryAction={
              agent?.role === 'super' && (
                <IconButton
                  edge="end"
                  aria-label="edit"
                  disabled={loading || loadingBalance}
                  onClick={() => setShowTenants(true)}
                >
                  <EditIcon />
                </IconButton>
              )
            }
          >
            <ListItemAvatar>
              <Avatar>
                <BusinessIcon />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={'Client'}
              secondary={
                <Typography variant="body2" color="text.secondary">
                  {loading
                    ? 'loading...'
                    : error
                    ? error.message
                    : tenant?.name}
                </Typography>
              }
            />
            {loading && <LinearProgress />}
          </ListItem>
          {loadingBalance && (
            <ListItem key="loadingBalance" alignItems="flex-start">
              <ListItemAvatar>
                <Avatar>
                  <CircularProgress />
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary="Loading balances..." />
            </ListItem>
          )}
          {accountBalance.map((item) => (
            <ListItem key={item.symbol} alignItems="flex-start">
              <ListItemAvatar>
                <Avatar>
                  <SavingsIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={`${item.title}`}
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    {item.amount}
                  </Typography>
                }
              />
            </ListItem>
          ))}
          <ListItem alignItems="flex-start">
            <ListItemAvatar>
              <Avatar>
                <FaceIcon />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={'Agent'}
              secondary={
                <Typography variant="body2" color="text.secondary">
                  {loading
                    ? 'loading...'
                    : error
                    ? error.message
                    : `${agent?.name} (${agent?.email})`}
                </Typography>
              }
            />
            {loading && <LinearProgress />}
          </ListItem>
        </List>
        <List
          sx={{ width: '100%', maxWidth: 700, bgcolor: 'background.paper' }}
        >
          Application
          <ListItem alignItems="flex-start">
            <ListItemAvatar>
              <Avatar>
                <SettingsIcon />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={'Version'}
              secondary={
                <Typography variant="body2" color="text.secondary">
                  {configs.version}
                </Typography>
              }
            />
          </ListItem>
        </List>
        {loading && <CircularProgress />}
      </Box>
      {agent?.role === 'super' && agent?.tenant && agent?.id && (
        <TenantChooserDialog
          open={showTenants}
          onClose={onCloseTenants}
          onNewSelected={onNewTenantSelected}
          tenantId={agent.tenant}
          agentId={agent.id}
        />
      )}
      <AlertDialog
        open={showTenantChange}
        severity="success"
        title="Platform Client Changed"
        message="You have successfully switched your platform client"
        onClose={() => {
          setShowTenantChange(false);
        }}
      />
      <Copyright />
    </Container>
  );
};

export default SettingsContainer;
